import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import ScrollReveal from "@/components/ScrollReveal";
import { Layers, Target, Compass, ArrowRight, Check, Shield } from "lucide-react";

const SERVICES_IMG = "https://static.prod-images.emergentagent.com/jobs/ce9ebb17-b340-4f23-afd3-bab0fd00e609/images/988431ce57a82950558eaa6a2956dd2ad9c545524575ca4ca874f1b1b3025a48.png";

export default function Services() {
  const { t } = useLanguage();
  const s = t.servicesPage;

  const services = [
    { data: s.ux, Icon: Layers, id: "ux" },
    { data: s.marketing, Icon: Target, id: "marketing" },
    { data: s.mentoring, Icon: Compass, id: "mentoring" },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 sm:pb-32 bg-[#2A2A2A]">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10 hidden lg:block">
          <img src={SERVICES_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <ScrollReveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {s.hero.overline}
            </p>
            <h1 data-testid="services-page-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-3xl">
              {s.hero.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/60 max-w-2xl">
              {s.hero.description}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Service Details */}
      {services.map(({ data, Icon, id }, idx) => (
        <section
          key={id}
          data-testid={`service-detail-${id}`}
          className={`py-24 sm:py-32 ${idx % 2 === 0 ? "bg-[#333333]" : "bg-[#2A2A2A]"}`}
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
              <div className="lg:col-span-5">
                <ScrollReveal>
                  <div className="w-14 h-14 rounded-sm bg-[#E8A400]/10 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-[#E8A400]" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-6">
                    {data.title}
                  </h2>
                  <p className="text-white/60 leading-relaxed mb-8">
                    {data.description}
                  </p>
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${id}`}
                    className="inline-flex items-center gap-2 bg-[#E8A400] text-[#1A1A1A] font-semibold px-8 py-3.5 hover:bg-[#C68C00] transition-colors duration-300 rounded-sm text-sm"
                  >
                    {data.cta}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </ScrollReveal>
              </div>
              <div className="lg:col-span-7">
                <ScrollReveal delay={150}>
                  <div className="border border-white/10 p-8">
                    <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-4">
                      {data.forWhomLabel}
                    </h3>
                    <p className="text-white/60 text-sm leading-relaxed mb-8">
                      {data.forWhom}
                    </p>
                    <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-4">
                      {data.outcomesLabel}
                    </h3>
                    <ul className="space-y-3">
                      {data.outcomes.map((outcome, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-[#E8A400] mt-0.5 shrink-0" />
                          <span className="text-sm text-white/70">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Guarantee */}
      <section data-testid="services-guarantee" className="py-24 sm:py-32 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 text-center">
          <ScrollReveal>
            <Shield className="w-10 h-10 text-[#E8A400] mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">
              {s.guarantee.title}
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto leading-relaxed">
              {s.guarantee.text}
            </p>
            <Link
              to="/contact"
              data-testid="services-final-cta"
              className="inline-flex items-center gap-2 bg-[#E8A400] text-[#1A1A1A] font-semibold px-10 py-4 hover:bg-[#C68C00] transition-colors duration-300 rounded-sm text-sm mt-10"
            >
              {t.finalCta.cta}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
